import { OnboardingSection } from '../OnboardingSection';
import { OnboardingSections } from '../../../constants';
import { Accordion } from '@rtkwlf/ui';
import * as React from 'react';
import OnboardingSiteList, { SiteCompleteButton } from './OnboardingSiteList';
import OnboardingSensorList, {
  SensorCompleteButton,
} from './OnboardingSensorList';
import OnboardingScannerList, {
  ScannerCompleteButton,
} from './OnboardingScannerList';
import OnboardingVirtualScanner, {
  VirtualScannerFooter,
} from './OnboardingVirtualScanner';
import { useDispatch, useSelector } from 'react-redux';
import { onboardingSelector } from '../../../reducers/onboarding';
import { useOBAccordionChange, withTrashableContext } from '../../../hooks';
import { OnboardingAccordionHeader } from '../OnboardingAccordionHeader';
import { getAccordionHeader, getPluralOrSingularNoun } from '../../../utils';
import { apiRequest } from '../../../APIClient';
import { OBHardwareType, PartnerTypeEnum } from '../../../types';
import { memoizedHardwareAllotmentSelectors } from '../../../selectors/hardwareSelectors';
import OnboardingVirtualSensorConfig, {
  VirtualSensorConfigFooter,
} from './OnboardingVirtualSensorConfig';
import {
  featureFlagSelector,
  sessionSelector,
} from '../../../reducers/session';
import { useFetchVirtualSensors } from '../../self-service/virtualsensor/useFetchVirtualSensors';
import OnboardingConnectWiseIntegrationConfig from './OnboardingConnectWiseIntegrationConfig';

export enum AccordionKeys {
  SITE_LIST = 'Site List',
  SENSOR_LIST = 'Sensor List',
  SCANNER_LIST = 'Physical Scanner List',
  VIRTUAL_SCANNER = 'Virtual Scanner Settings',
  VIRTUAL_SENSOR = 'Virtual Sensor Settings',
  VIRTUAL_LOG_COLLECTOR = 'Virtual Log Collector Settings',
  CONNECTWISE_INTEGRATION = 'ConnectWise Integration',
}

export enum ScrollSectionKeys {
  CONTACTS = 'contacts',
  SENSORS = 'sensors',
  SCANNERS = 'scanners',
}

export const OnboardingSiteConfig = () => {
  const dispatch = useDispatch();

  const sites = useSelector(onboardingSelector.getSites);
  const sitesCompleted = useSelector(onboardingSelector.getSitesCompleted);

  const hasMDR = useSelector(onboardingSelector.isManagedDetectionEnabled);
  const sensors = useSelector(onboardingSelector.getSensors);
  const sensorsCompleted = useSelector(onboardingSelector.getSensorsCompleted);

  const hasMR = useSelector(onboardingSelector.isManagedRiskEnabled);
  const boughtPhysicalScanners =
    Object.keys(
      useSelector(memoizedHardwareAllotmentSelectors[OBHardwareType.SCANNER]),
    ).length > 0;
  const scanners = useSelector(onboardingSelector.getScanners);
  const scannersCompleted = useSelector(
    onboardingSelector.getScannersCompleted,
  );
  const partnerType = useSelector(sessionSelector.getCustomerPartnerType);
  const isMSPCustomer =
    partnerType &&
    (partnerType === PartnerTypeEnum.MSP ||
      partnerType === PartnerTypeEnum.MSP_PLUS);
  const virtual = useSelector(onboardingSelector.getVirtualScanner);
  const sensorConfig = useSelector(onboardingSelector.getVirtualSensorConfig);
  // Notes on vSensor / VLC section:
  // if hasMDR:
  //    if virtualSensorsEnabled:
  //        if vSensorQuota > 0: show vSensor
  //        else: show neither
  //    else: show VLC
  // else: show neither
  const virtualSensorsEnabled = useSelector(
    featureFlagSelector.onboarding.virtualSensors,
  );

  const { vSensors } = useFetchVirtualSensors(virtualSensorsEnabled);
  const { quota: awnv100Quota = 0, used: awnv100Used = 0 } =
    vSensors?.awnv100 ?? {};
  const { quota: awnv200Quota = 0, used: awnv200Used = 0 } =
    vSensors?.awnv200 ?? vSensors?.vsensor ?? vSensors?.vSensor ?? {};
  const { quota: awnv1000Quota = 0, used: awnv1000Used = 0 } =
    vSensors?.awnv1000 ?? {};
  const vSensorQuota = awnv100Quota + awnv200Quota + awnv1000Quota;
  const vSensorUsed = awnv100Used + awnv200Used + awnv1000Used;

  const showVSensor = virtualSensorsEnabled && vSensorQuota > 0;
  const showVLC = !virtualSensorsEnabled;

  const filteredAccordionItems = React.useMemo(() => {
    let items = Object.values(AccordionKeys);

    items = items.filter((obj) => {
      if (!showVSensor && obj === AccordionKeys.VIRTUAL_SENSOR) return false;
      if (!showVLC && obj === AccordionKeys.VIRTUAL_LOG_COLLECTOR) return false;
      if (!isMSPCustomer && obj === AccordionKeys.CONNECTWISE_INTEGRATION)
        return false;
      return true;
    });

    return items;
  }, [showVLC, showVSensor, isMSPCustomer]);

  const { collapseAll, isCollapsed, expandAll, expandedItems, handleOnChange } =
    useOBAccordionChange(filteredAccordionItems);

  React.useEffect(() => {
    dispatch(apiRequest.getOnboardingAvailableHardware());
  }, [dispatch]);

  const physicalScannerLength = Object.values(scanners).length;
  const sensorLength = Object.values(sensors).length;
  const sitesLength = Object.values(sites).length;
  const siteListTitle = getAccordionHeader(
    sitesCompleted,
    expandedItems,
    AccordionKeys.SITE_LIST,
    sitesLength === 0
      ? 'No sites'
      : getPluralOrSingularNoun(Object.values(sites).length, 'site'),
    'Site list incomplete',
  );
  const sensorListTitle = getAccordionHeader(
    sensorsCompleted,
    expandedItems,
    AccordionKeys.SENSOR_LIST,
    sensorLength === 0
      ? 'No sensors'
      : getPluralOrSingularNoun(sensorLength, 'sensor'),
    'Sensor list incomplete',
  );
  const scannerListTitle = getAccordionHeader(
    scannersCompleted,
    expandedItems,
    AccordionKeys.SCANNER_LIST,
    physicalScannerLength === 0
      ? 'No physical scanners'
      : getPluralOrSingularNoun(physicalScannerLength, 'physical scanner'),
    'Physical scanner list incomplete',
  );
  const virtualScannerTitle = getAccordionHeader(
    virtual.completed,
    expandedItems,
    AccordionKeys.VIRTUAL_SCANNER,
    virtual.noVirtualScanners
      ? 'No virtual scanners'
      : 'Virtual scanner configuration complete',
    'Virtual scanner configuration incomplete',
  );
  const virtualSensorTitle = getAccordionHeader(
    sensorConfig.completed,
    expandedItems,
    virtualSensorsEnabled
      ? AccordionKeys.VIRTUAL_SENSOR
      : AccordionKeys.VIRTUAL_LOG_COLLECTOR,
    sensorConfig.noVirtualSensor
      ? 'No virtual log collectors'
      : virtualSensorsEnabled
      ? 'Virtual sensor settings complete'
      : 'Virtual log collector settings complete',
    virtualSensorsEnabled
      ? 'Virtual sensor settings incomplete'
      : 'Virtual log collector settings incomplete',
  );
  return (
    <OnboardingSection
      title={OnboardingSections.SITE_CONFIG}
      completed={
        sitesCompleted &&
        sensorsCompleted &&
        scannersCompleted &&
        virtual.completed
      }
    >
      <OnboardingAccordionHeader
        description="This information helps us to build a comprehensive understanding of your wide area network."
        isCollapsed={isCollapsed}
        expandAll={expandAll}
        collapseAll={collapseAll}
      />
      <div className="ob-accordion-section">
        <Accordion
          itemGap={20}
          expandedItems={expandedItems}
          onChange={handleOnChange}
        >
          <Accordion.Item
            className="mr-3"
            itemKey={AccordionKeys.SITE_LIST}
            header={siteListTitle}
            footer={<SiteCompleteButton />}
          >
            <OnboardingSiteList />
          </Accordion.Item>
          {hasMDR && (
            <Accordion.Item
              className="mr-3"
              itemKey={AccordionKeys.SENSOR_LIST}
              header={sensorListTitle}
              footer={<SensorCompleteButton />}
            >
              <OnboardingSensorList />
            </Accordion.Item>
          )}
          {hasMR && boughtPhysicalScanners && (
            <Accordion.Item
              className="mr-3"
              itemKey={AccordionKeys.SCANNER_LIST}
              header={scannerListTitle}
              footer={<ScannerCompleteButton />}
            >
              <OnboardingScannerList />
            </Accordion.Item>
          )}
          {hasMR && (
            <Accordion.Item
              className="mr-3"
              itemKey={AccordionKeys.VIRTUAL_SCANNER}
              header={virtualScannerTitle}
              footer={<VirtualScannerFooter />}
            >
              <OnboardingVirtualScanner />
            </Accordion.Item>
          )}
          {hasMDR && (showVLC || showVSensor) && (
            <Accordion.Item
              className="mr-3"
              itemKey={
                virtualSensorsEnabled
                  ? AccordionKeys.VIRTUAL_SENSOR
                  : AccordionKeys.VIRTUAL_LOG_COLLECTOR
              }
              header={virtualSensorTitle}
              footer={<VirtualSensorConfigFooter />}
            >
              <OnboardingVirtualSensorConfig
                vSensorQuota={vSensorQuota}
                vSensorUsed={vSensorUsed}
              />
            </Accordion.Item>
          )}
          {isMSPCustomer && (
            <Accordion.Item
              className="mr-3"
              itemKey={AccordionKeys.CONNECTWISE_INTEGRATION}
              header="ConnectWise Integration"
            >
              <OnboardingConnectWiseIntegrationConfig />
            </Accordion.Item>
          )}
        </Accordion>
      </div>
    </OnboardingSection>
  );
};

export default withTrashableContext(OnboardingSiteConfig);
